export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  /** Valgfri knap i toasten (fx "Fortryd"); toasten lukkes, når der klikkes. Står mindst `ACTION_TOAST_MS`. */
  action?: { label: string; onClick: () => void };
}

type Listener = (toast: ToastMessage) => void;

/** Mindste visningstid for en toast med en handling (tid til at nå knappen med tastaturet). */
export const ACTION_TOAST_MS = 12000;

const listeners = new Set<Listener>();
let counter = 0;

/** Abonner paa toasts. Returnerer en afmeldingsfunktion. */
export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Send en toast. Kan kaldes fra almindelige moduler (fx api.ts), ikke kun fra
 * React-komponenter.
 */
export function pushToast(
  message: string,
  type: ToastType = 'info',
  duration = 4000,
  action?: ToastMessage['action'],
): string {
  counter += 1;
  // En toast med en handling står mindst 12 s (0 = indtil den lukkes, uændret).
  const shown = action && duration > 0 ? Math.max(duration, ACTION_TOAST_MS) : duration;
  const toast: ToastMessage = { id: `t${counter}`, type, message, duration: shown, ...(action ? { action } : {}) };
  listeners.forEach((listener) => listener(toast));
  return toast.id;
}

type DismissListener = (id: string) => void;
const dismissListeners = new Set<DismissListener>();

/** Abonner paa lukning af en toast udefra (`dismissToast`). Returnerer en afmeldingsfunktion. */
export function subscribeToastDismiss(listener: DismissListener): () => void {
  dismissListeners.add(listener);
  return () => {
    dismissListeners.delete(listener);
  };
}

/** Lukker en vist toast (fx Fortryd-toasten, naar handlingen er fortrudt fra sidehovedet). */
export function dismissToast(id: string): void {
  dismissListeners.forEach((listener) => listener(id));
}

export const toast = {
  success: (message: string, duration?: number, action?: ToastMessage['action']) =>
    pushToast(message, 'success', duration, action),
  error: (message: string, duration?: number) => pushToast(message, 'error', duration ?? 6000),
  warning: (message: string, duration?: number) => pushToast(message, 'warning', duration),
  info: (message: string, duration?: number) => pushToast(message, 'info', duration),
};
